import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  FamilyTree,
  TreeViewMode,
  D3TreeNode,
  D3TreeLink,
  TreeRenderData,
  TreeViewConfig,
  TreeLayoutAlgorithm,
  DEFAULT_TREE_CONFIG,
} from '../../types';
import { TreeLayoutService } from '../../services/treeLayoutService';
import { TreeNodeSVG } from './TreeNodeSVG';

/**
 * InteractiveTreeView Component
 *
 * D3-powered interactive family tree visualization
 *
 * Features:
 * - Pan and zoom navigation
 * - Multiple layout algorithms (tree, cluster, radial)
 * - Node expand/collapse
 * - Search with highlighting
 * - Lineage path highlighting
 * - Click navigation to profiles
 * - Smooth D3 animations
 * - Performance optimized for 500+ nodes
 *
 * @component
 */
interface InteractiveTreeViewProps {
  /** Family tree data */
  tree: FamilyTree;

  /** View mode */
  viewMode: TreeViewMode;

  /** Layout algorithm */
  layoutAlgorithm?: TreeLayoutAlgorithm;

  /** Configuration */
  config?: Partial<TreeViewConfig>;

  /** Person click handler */
  onPersonClick: (personId: string) => void;

  /** Node expand/collapse handler */
  onNodeToggle?: (personId: string) => void;

  /** Person hover handler */
  onPersonHover?: (personId: string | null) => void;

  /** Currently selected person */
  selectedPersonId?: string;

  /** Highlighted person IDs (e.g., from search) */
  highlightedPersonIds?: string[];

  /** Lineage path person IDs */
  lineagePersonIds?: string[];

  /** Enable expand/collapse */
  enableCollapse?: boolean;

  /** External zoom control */
  zoomLevel?: number;

  /** External pan control */
  panPosition?: { x: number; y: number };

  /** Fit to view trigger */
  fitToView?: boolean;

  /** Center on person ID trigger */
  centerOnPerson?: string;
}

export const InteractiveTreeView: React.FC<InteractiveTreeViewProps> = ({
  tree,
  viewMode,
  layoutAlgorithm = 'tree',
  config: userConfig,
  onPersonClick,
  onNodeToggle,
  onPersonHover,
  selectedPersonId,
  highlightedPersonIds = [],
  lineagePersonIds = [],
  enableCollapse = true,
  zoomLevel,
  panPosition,
  fitToView,
  centerOnPerson,
}) => {
  // ===== Refs =====
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const gRef = useRef<SVGGElement>(null);
  const zoomBehaviorRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);

  // ===== State =====
  const [renderData, setRenderData] = useState<TreeRenderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);

  // Merge default config with user config (memoized to prevent unnecessary re-renders)
  const config: TreeViewConfig = useMemo(() => ({
    ...DEFAULT_TREE_CONFIG,
    ...userConfig,
  }), [userConfig]);

  // ===== Effects =====

  /**
   * Calculate layout when tree or config changes
   */
  useEffect(() => {
    if (!tree || !tree.root) {
      setError('No tree data available');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get current container size
      let layoutConfig = config;
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        layoutConfig = {
          ...config,
          width: rect.width || 1200,
          height: Math.max(rect.height || 800, 600),
        };
      }

      // Calculate layout
      const data = TreeLayoutService.calculateLayout(
        tree.root,
        layoutConfig,
        layoutAlgorithm
      );

      setRenderData(data);
      setLoading(false);
    } catch (err: any) {
      console.error('Failed to calculate tree layout:', err);
      setError(err.message || 'Failed to calculate tree layout');
      setLoading(false);
    }
  }, [tree, viewMode, layoutAlgorithm, config.orientation, config.nodeWidth, config.nodeHeight, config.siblingSeparation, config.nonSiblingSeparation]);

  /**
   * Initialize D3 zoom behavior
   */
  useEffect(() => {
    if (!svgRef.current || !gRef.current || !renderData) {
      return;
    }

    // Don't set up zoom if both are disabled
    if (!config.enableZoom && !config.enablePan) {
      return;
    }

    const svg = d3.select(svgRef.current);
    const g = d3.select(gRef.current);

    // Create zoom behavior (D3's zoom handles both pan and zoom)
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([config.minZoom, config.maxZoom])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    // Apply zoom behavior to the SVG
    svg.call(zoom);

    // Store reference for external zoom controls
    zoomBehaviorRef.current = zoom;

    // Set initial transform only if there's no existing transform
    const currentTransform = d3.zoomTransform(svgRef.current);
    if (currentTransform.k === 1 && currentTransform.x === 0 && currentTransform.y === 0) {
      const initialTransform = d3.zoomIdentity
        .translate(100, 100)
        .scale(config.initialZoom);
      svg.call(zoom.transform, initialTransform);
    }

    return () => {
      svg.on('.zoom', null);
    };
  }, [renderData, config.enableZoom, config.enablePan, config.minZoom, config.maxZoom, config.initialZoom]);

  /**
   * Handle external zoom level changes
   */
  useEffect(() => {
    if (zoomLevel !== undefined && svgRef.current && zoomBehaviorRef.current) {
      const svg = d3.select(svgRef.current);
      svg.transition()
        .duration(config.animationDuration)
        .call(zoomBehaviorRef.current.scaleTo, zoomLevel);
    }
  }, [zoomLevel, config.animationDuration]);

  /**
   * Handle external pan position changes
   */
  useEffect(() => {
    if (panPosition && svgRef.current && zoomBehaviorRef.current) {
      const svg = d3.select(svgRef.current);
      const currentTransform = d3.zoomTransform(svgRef.current);
      const newTransform = currentTransform.translate(panPosition.x, panPosition.y);

      svg.transition()
        .duration(config.animationDuration)
        .call(zoomBehaviorRef.current.transform, newTransform);
    }
  }, [panPosition, config.animationDuration]);

  /**
   * Handle fit to view
   */
  useEffect(() => {
    if (fitToView && renderData && svgRef.current && zoomBehaviorRef.current) {
      fitTreeToView();
    }
  }, [fitToView]);

  /**
   * Handle center on person
   */
  useEffect(() => {
    if (centerOnPerson && renderData && svgRef.current && zoomBehaviorRef.current) {
      centerOnPersonNode(centerOnPerson);
    }
  }, [centerOnPerson]);

  // ===== Callbacks =====

  /**
   * Fit entire tree to view
   */
  const fitTreeToView = useCallback(() => {
    if (!renderData || !svgRef.current || !zoomBehaviorRef.current || !containerRef.current) {
      return;
    }

    const svg = d3.select(svgRef.current);
    const container = containerRef.current.getBoundingClientRect();

    // Get bounds of all nodes
    const nodes = renderData.nodes;
    if (nodes.length === 0) return;

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    nodes.forEach(node => {
      minX = Math.min(minX, node.x);
      maxX = Math.max(maxX, node.x);
      minY = Math.min(minY, node.y);
      maxY = Math.max(maxY, node.y);
    });

    // Add padding
    const padding = 50;
    const contentWidth = maxX - minX + padding * 2;
    const contentHeight = maxY - minY + padding * 2;

    // Calculate scale to fit
    const scaleX = container.width / contentWidth;
    const scaleY = container.height / contentHeight;
    const scale = Math.min(scaleX, scaleY, config.maxZoom);

    // Calculate translation to center
    const translateX = (container.width - contentWidth * scale) / 2 - minX * scale + padding * scale;
    const translateY = (container.height - contentHeight * scale) / 2 - minY * scale + padding * scale;

    // Apply transform
    const transform = d3.zoomIdentity
      .translate(translateX, translateY)
      .scale(scale);

    svg.transition()
      .duration(config.animationDuration)
      .call(zoomBehaviorRef.current.transform, transform);
  }, [renderData, config.animationDuration, config.maxZoom]);

  /**
   * Center view on specific person node
   */
  const centerOnPersonNode = useCallback((personId: string) => {
    if (!renderData || !svgRef.current || !zoomBehaviorRef.current || !containerRef.current) {
      return;
    }

    const node = renderData.nodes.find(n => n.data.person.id === personId);
    if (!node) return;

    const svg = d3.select(svgRef.current);
    const container = containerRef.current.getBoundingClientRect();
    const currentTransform = d3.zoomTransform(svgRef.current);

    // Calculate translation to center the node
    const translateX = container.width / 2 - node.x * currentTransform.k;
    const translateY = container.height / 2 - node.y * currentTransform.k;

    const transform = d3.zoomIdentity
      .translate(translateX, translateY)
      .scale(currentTransform.k);

    svg.transition()
      .duration(config.animationDuration)
      .call(zoomBehaviorRef.current.transform, transform);
  }, [renderData, config.animationDuration]);

  // ===== Event Handlers =====

  const handleNodeClick = useCallback((node: D3TreeNode, event: React.MouseEvent) => {
    event.stopPropagation();
    onPersonClick(node.data.person.id);
  }, [onPersonClick]);

  const handleNodeDoubleClick = useCallback((node: D3TreeNode, event: React.MouseEvent) => {
    event.stopPropagation();
    if (enableCollapse && onNodeToggle) {
      onNodeToggle(node.data.person.id);
    }
  }, [enableCollapse, onNodeToggle]);

  const handleNodeMouseEnter = useCallback((node: D3TreeNode, event: React.MouseEvent) => {
    setHoveredNodeId(node.data.person.id);
    onPersonHover?.(node.data.person.id);
  }, [onPersonHover]);

  const handleNodeMouseLeave = useCallback((node: D3TreeNode, event: React.MouseEvent) => {
    setHoveredNodeId(null);
    onPersonHover?.(null);
  }, [onPersonHover]);

  // ===== Helper Functions =====

  const isNodeHighlighted = (nodeId: string): boolean => {
    return highlightedPersonIds.includes(nodeId);
  };

  const isNodeInLineage = (nodeId: string): boolean => {
    return lineagePersonIds.includes(nodeId);
  };

  const isNodeSelected = (nodeId: string): boolean => {
    return nodeId === selectedPersonId;
  };

  const getLinkPath = (link: D3TreeLink): string => {
    return TreeLayoutService.calculateLinkPaths(
      [link],
      config.orientation
    )[0]?.d || '';
  };

  // ===== Render =====

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 400,
          textAlign: 'center',
          p: 3,
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!renderData || renderData.nodes.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 400,
        }}
      >
        <Typography color="text.secondary">No tree data to display</Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 600,
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#FAFAFA',
        borderRadius: 1,
      }}
    >
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        style={{ cursor: config.enablePan ? 'grab' : 'default' }}
      >
        <defs>
          {/* Gradient for links */}
          <linearGradient id="link-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#CCCCCC" stopOpacity={0.8} />
            <stop offset="100%" stopColor="#999999" stopOpacity={0.8} />
          </linearGradient>

          {/* Highlighted link gradient */}
          <linearGradient id="link-highlight-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#90EE90" stopOpacity={1} />
            <stop offset="100%" stopColor="#32CD32" stopOpacity={1} />
          </linearGradient>

          {/* Arrow marker for links */}
          <marker
            id="arrow"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#999999" />
          </marker>
        </defs>

        <g ref={gRef}>
          {/* Render links first (behind nodes) */}
          {renderData.links.map((link, index) => {
            const isHighlighted = isNodeInLineage(link.source.data.person.id) &&
                                 isNodeInLineage(link.target.data.person.id);

            // Determine link style based on type and biological status
            let strokeColor = 'url(#link-gradient)';
            let strokeDasharray = 'none';
            let strokeWidth = 2;

            if (link.type === 'spouse') {
              // Spouse links: purple horizontal line
              strokeColor = '#9370DB';
              strokeDasharray = 'none';
              strokeWidth = 3;
            } else if (link.type === 'sibling') {
              // Sibling links: different colors based on sibling type
              if (link.biologicalStatus === 'biological') {
                // Full/half siblings: solid teal line
                strokeColor = '#20B2AA';
                strokeDasharray = 'none';
                strokeWidth = 2;
              } else if (link.biologicalStatus === 'step') {
                // Step siblings: dotted slate blue line
                strokeColor = '#6A5ACD';
                strokeDasharray = '4,4';
                strokeWidth = 2;
              } else if (link.biologicalStatus === 'adoptive') {
                // Adoptive siblings: dashed coral line
                strokeColor = '#FF7F50';
                strokeDasharray = '6,3';
                strokeWidth = 2;
              }
            } else if (link.biologicalStatus === 'adoptive') {
              // Adoptive relationships: dashed orange line
              strokeColor = '#FF8C00';
              strokeDasharray = '8,4';
            } else if (link.biologicalStatus === 'step') {
              // Step relationships: dotted steel blue line
              strokeColor = '#4682B4';
              strokeDasharray = '4,4';
            } else if (link.biologicalStatus === 'foster') {
              // Foster relationships: dashed green line
              strokeColor = '#32CD32';
              strokeDasharray = '6,3';
            }

            // Override with highlight if needed
            if (isHighlighted) {
              strokeColor = 'url(#link-highlight-gradient)';
              strokeWidth = 3;
            }

            return (
              <path
                key={`link-${index}`}
                d={getLinkPath(link)}
                fill="none"
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                strokeDasharray={strokeDasharray}
                strokeOpacity={isHighlighted ? 1 : 0.7}
                style={{
                  transition: 'all 0.3s ease',
                }}
              />
            );
          })}

          {/* Render nodes */}
          {renderData.nodes.map((node) => (
            <TreeNodeSVG
              key={(node.data as any)._syntheticId || node.data.person.id}
              node={node}
              width={config.nodeWidth}
              height={config.nodeHeight}
              onClick={handleNodeClick}
              onDoubleClick={handleNodeDoubleClick}
              onMouseEnter={handleNodeMouseEnter}
              onMouseLeave={handleNodeMouseLeave}
              isHighlighted={isNodeHighlighted(node.data.person.id)}
              isInLineage={isNodeInLineage(node.data.person.id)}
              isSelected={isNodeSelected(node.data.person.id)}
              enableCollapse={enableCollapse}
            />
          ))}
        </g>
      </svg>

      {/* Statistics Overlay */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          bgcolor: 'rgba(255,255,255,0.9)',
          px: 2,
          py: 1,
          borderRadius: 1,
          boxShadow: 1,
          fontSize: '0.875rem',
          color: 'text.secondary',
        }}
      >
        {renderData.visibleNodeCount} persons • {renderData.linkCount} connections
      </Box>
    </Box>
  );
};
